import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import { buildStaticMapUrl } from '../api/graderApi';

const EASE = [0.22, 1, 0.36, 1];

/*
 * The scan viewport requests a fixed-center static map (640x360) and overlays
 * its own animated pins, so competitor positions need deterministic Web
 * Mercator math instead of Google's auto-fit.
 */
const MAP_W = 640;
const MAP_H = 360;

const mercator = (lat, lng) => {
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: 256 * (0.5 + lng / 360),
    y: 256 * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))
  };
};

// Picks the tightest zoom (15 -> 10) that keeps every competitor inside a safe
// inner box, then converts each to % offsets from the map center.
const projectPins = (center, points) => {
  if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) {
    return { zoom: null, pins: [] };
  }
  const c = mercator(center.lat, center.lng);
  for (let zoom = 15; zoom >= 10; zoom -= 1) {
    const scale = 2 ** zoom;
    const placed = points
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && p.lat !== 0 && p.lng !== 0)
      .map((p) => {
        const m = mercator(p.lat, p.lng);
        return { ...p, dx: (m.x - c.x) * scale, dy: (m.y - c.y) * scale };
      });
    const fits = placed.every((p) => Math.abs(p.dx) < MAP_W * 0.42 && Math.abs(p.dy) < MAP_H * 0.36);
    if (fits || zoom === 10) {
      return {
        zoom,
        pins: placed.map((p) => ({
          ...p,
          leftPct: Math.min(94, Math.max(6, 50 + (p.dx / MAP_W) * 100)),
          topPct: Math.min(88, Math.max(10, 50 + (p.dy / MAP_H) * 100))
        }))
      };
    }
  }
  return { zoom: 12, pins: [] };
};

const STAGE_TICK_MS = {
  loading: 600,
  photos: 850,
  reviews: 1700,
  map: 950
};

// Deterministic "messy pile" tilts/offsets so photos and reviews land like
// prints tossed on a desk - stable across re-renders, no randomness.
const PHOTO_TILTS = [-5, 4, -2.5, 6, -4, 2.5];
const PHOTO_OFFSETS = [[-10, -6], [12, 8], [-14, 10], [8, -10], [0, 12], [-6, 2]];
const REVIEW_TILTS = [-2.5, 2, -1.5, 2.8, -2];
const REVIEW_OFFSETS = [[-6, -4], [8, 6], [-8, 8], [5, -6], [0, 4]];

const StageStars = ({ rating }) => (
  <span className="vlg-stagerev__stars" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        size={15}
        strokeWidth={1.6}
        className={star <= Math.round(rating) ? 'is-filled' : ''}
        aria-hidden="true"
      />
    ))}
  </span>
);

const ScanningPhase = ({
  selectedBusiness,
  currentScanStepLabel,
  scanProgress,
  visibleScanActivity,
  SCAN_STEPS,
  scanStepIndex,
  scanVisuals,
  mapEmbedUrl,
  mapZoomTransform
}) => {
  // The bar climbs on real step state, but never fake-hits 100% and hangs while
  // we wait on the API - it caps just shy of done until the preview replaces it.
  const lastStep = SCAN_STEPS.length - 1;
  const finalizing = scanStepIndex >= lastStep;
  const displayPct = Math.min(Math.max(scanProgress, 6), 96);

  const city = selectedBusiness?.city && selectedBusiness.city !== 'N/A' ? selectedBusiness.city : '';
  const statusLine = finalizing
    ? 'Compiling your competition-adjusted grade…'
    : (visibleScanActivity && visibleScanActivity[0]) || `${currentScanStepLabel}…`;

  const photos = scanVisuals?.photos || [];

  // Interleave lowest and highest rated reviews so both extremes get screen
  // time: [lowest, highest, 2nd-lowest, 2nd-highest].
  const orderedReviews = useMemo(() => {
    const source = (scanVisuals?.reviews?.length
      ? scanVisuals.reviews
      : (selectedBusiness?.reviewSnippets || []).map((snippet) => ({
        author: '', rating: snippet.rating, text: snippet.text, when: ''
      }))
    ).filter((review) => review.text && review.rating > 0);

    const byRating = [...source].sort((a, b) => a.rating - b.rating);
    const lows = byRating.slice(0, 2);
    const highs = byRating.slice(-2).reverse().filter((review) => !lows.includes(review));
    const mixed = [];
    for (let i = 0; i < Math.max(lows.length, highs.length); i += 1) {
      if (lows[i]) mixed.push({ ...lows[i], tone: lows[i].rating <= 3 ? 'low' : 'high' });
      if (highs[i]) mixed.push({ ...highs[i], tone: 'high' });
    }
    return mixed;
  }, [scanVisuals, selectedBusiness]);

  const mapCenter = useMemo(() => {
    const lat = Number(scanVisuals?.location?.lat) || Number(selectedBusiness?.lat) || 0;
    const lng = Number(scanVisuals?.location?.lng) || Number(selectedBusiness?.lng) || 0;
    return lat !== 0 && lng !== 0 ? { lat, lng } : null;
  }, [scanVisuals, selectedBusiness]);

  const { zoom: mapZoom, pins } = useMemo(
    () => projectPins(mapCenter, scanVisuals?.competitors || []),
    [mapCenter, scanVisuals]
  );

  const staticMapUrl = useMemo(() => {
    if (!mapCenter || !mapZoom) return '';
    return buildStaticMapUrl({ you: `${mapCenter.lat},${mapCenter.lng}`, zoom: mapZoom });
  }, [mapCenter, mapZoom]);

  const [staticMapFailed, setStaticMapFailed] = useState(false);

  // Sequence: photos while the profile steps run, reviews during the sentiment
  // steps, then the competitor map to the finish. Empty stages are skipped.
  const stage = useMemo(() => {
    const hasPhotos = photos.length > 0;
    const hasReviews = orderedReviews.length > 0;
    if (scanStepIndex <= 1) {
      if (hasPhotos) return 'photos';
      if (!scanVisuals) return 'loading';
      return hasReviews ? 'reviews' : 'map';
    }
    if (scanStepIndex <= 3) {
      if (hasReviews) return 'reviews';
      return hasPhotos ? 'photos' : 'map';
    }
    return 'map';
  }, [scanStepIndex, photos.length, orderedReviews.length, scanVisuals]);

  // Stage-local ticker drives the sequential reveals (photo fade-ins, review
  // rotation, competitor pin drops).
  const [stageTick, setStageTick] = useState(0);
  useEffect(() => {
    setStageTick(0);
    const interval = setInterval(
      () => setStageTick((tick) => tick + 1),
      STAGE_TICK_MS[stage] || 900
    );
    return () => clearInterval(interval);
  }, [stage]);

  const revealedPhotos = Math.min(photos.length, stageTick + 1);
  const revealedReviews = Math.min(orderedReviews.length, stageTick + 1);
  const visiblePins = Math.min(pins.length, stageTick);

  const stageHeading = stage === 'photos'
    ? 'What Google shows customers'
    : stage === 'reviews'
      ? 'What your reviews are saying'
      : 'Mapping your local competitors';

  const useStaticMap = Boolean(staticMapUrl) && !staticMapFailed;

  return (
    <motion.div
      className="vlg-phase"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.32, ease: EASE }}
    >
      <div className="vlg-scan2">
        <header className="vlg-scan2__head">
          <p className="vlg-scan__name">
            {selectedBusiness?.name ? `Scanning${city ? ` · ${city}` : ''}` : 'Running your scan'}
          </p>
          <h2 className="vlg-scan__title">
            {selectedBusiness?.name || 'Your visibility scan'}
          </h2>
        </header>

        <aside className="vlg-scan2__panel">
          <div className="vlg-scanbar">
            <div className="vlg-scanbar__head">
              <span className="vlg-scanbar__label">{finalizing ? 'Finalizing your report' : currentScanStepLabel}</span>
              <span className="vlg-scanbar__pct">{displayPct}%</span>
            </div>
            <div className="vlg-scanbar__track" aria-hidden="true">
              <div className="vlg-scanbar__fill" style={{ width: `${displayPct}%` }} />
            </div>
          </div>

          <p className="vlg-scan__status" role="status" aria-live="polite">{statusLine}</p>

          <ul className="vlg-checks" aria-label="Scan checks">
            {SCAN_STEPS.map((step, index) => {
              const done = index < scanStepIndex;
              const active = index === scanStepIndex;
              const revealed = index <= scanStepIndex;
              const cls = `vlg-check${revealed ? ' is-revealed' : ''}${done ? ' is-done' : ''}${active ? ' is-active' : ''}`;
              return (
                <li key={step} className={cls}>
                  <span className="vlg-check__marker">
                    {done ? (
                      <Check className="vlg-check__check" size={18} strokeWidth={2.6} aria-hidden="true" />
                    ) : (
                      <span className="vlg-check__dot" aria-hidden="true" />
                    )}
                  </span>
                  <span className="vlg-check__label">{step}</span>
                  <span className="vlg-check__state">
                    {done ? 'Done' : active ? 'Checking…' : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </aside>

        <div className="vlg-scan2__stage" aria-hidden="true">
          <p className="vlg-stage__heading">{stageHeading}</p>

          <AnimatePresence mode="wait">
            {stage === 'loading' && (
              <motion.div
                key="stage-loading"
                className="vlg-stage vlg-stage--loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: EASE }}
              >
                <div className="vlg-stage__shimmer" />
              </motion.div>
            )}

            {stage === 'photos' && (
              <motion.div
                key="stage-photos"
                className="vlg-stage vlg-stage--photos"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.32, ease: EASE }}
              >
                <div className="vlg-photostack">
                  {photos.map((src, index) => {
                    const tilt = PHOTO_TILTS[index % PHOTO_TILTS.length];
                    const [ox, oy] = PHOTO_OFFSETS[index % PHOTO_OFFSETS.length];
                    return (
                      <div
                        key={src}
                        className="vlg-photostack__item"
                        style={{ left: `calc(50% + ${ox}px)`, top: `calc(50% + ${oy}px)`, zIndex: index + 1 }}
                      >
                        <motion.div
                          className="vlg-photostack__card"
                          initial={{ opacity: 0, scale: 1.14, rotate: tilt + 9, y: -34 }}
                          animate={index < revealedPhotos
                            ? { opacity: 1, scale: 1, rotate: tilt, y: 0 }
                            : { opacity: 0, scale: 1.14, rotate: tilt + 9, y: -34 }}
                          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                        >
                          <img src={src} alt="" loading="eager" />
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
                <p className="vlg-stage__caption">Pulling the photos customers see on your Google listing</p>
              </motion.div>
            )}

            {stage === 'reviews' && (
              <motion.div
                key="stage-reviews"
                className="vlg-stage vlg-stage--reviews"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.32, ease: EASE }}
              >
                <div className="vlg-revstack">
                  {orderedReviews.map((review, index) => {
                    const tilt = REVIEW_TILTS[index % REVIEW_TILTS.length];
                    const [ox, oy] = REVIEW_OFFSETS[index % REVIEW_OFFSETS.length];
                    return (
                      <div
                        key={`review-${index}`}
                        className="vlg-revstack__item"
                        style={{ left: `calc(50% + ${ox}px)`, top: `calc(50% + ${oy}px)`, zIndex: index + 1 }}
                      >
                        <motion.figure
                          className={`vlg-stagerev vlg-stagerev--${review.tone}`}
                          initial={{ opacity: 0, y: 38, scale: 0.94, rotate: tilt + 4 }}
                          animate={index < revealedReviews
                            ? { opacity: 1, y: 0, scale: 1, rotate: tilt }
                            : { opacity: 0, y: 38, scale: 0.94, rotate: tilt + 4 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                        >
                          <div className="vlg-stagerev__top">
                            <StageStars rating={review.rating} />
                            <span className="vlg-stagerev__tag">
                              {review.tone === 'low' ? 'Critical review' : 'Top review'}
                            </span>
                          </div>
                          <blockquote className="vlg-stagerev__text">&ldquo;{review.text}&rdquo;</blockquote>
                          <figcaption className="vlg-stagerev__meta">
                            {review.author || 'Google review'}
                            {review.when ? ` · ${review.when}` : ''}
                          </figcaption>
                        </motion.figure>
                      </div>
                    );
                  })}
                </div>
                <p className="vlg-stage__caption">Reading your highest and lowest reviews for sentiment</p>
              </motion.div>
            )}

            {stage === 'map' && (
              <motion.div
                key="stage-map"
                className="vlg-stage vlg-stage--map"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.32, ease: EASE }}
              >
                <div className="vlg-scanmap vlg-scanmap--stage">
                  {useStaticMap ? (
                    <img
                      className="vlg-scanmap__img"
                      src={staticMapUrl}
                      alt=""
                      onError={() => setStaticMapFailed(true)}
                    />
                  ) : mapEmbedUrl ? (
                    <iframe
                      title="Scanning location"
                      src={mapEmbedUrl}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      style={{ transform: mapZoomTransform }}
                    />
                  ) : null}
                  <div className="vlg-scanmap__veil" />
                  <div className="vlg-scanmap__pin">
                    <span className="vlg-scanmap__pulse" />
                    <span className="vlg-scanmap__dot" />
                  </div>
                  {useStaticMap && pins.slice(0, visiblePins).map((pin) => (
                    <div
                      key={pin.id || pin.name}
                      className="vlg-mappin"
                      style={{ left: `${pin.leftPct}%`, top: `${pin.topPct}%` }}
                    >
                      <motion.div
                        className="vlg-mappin__inner"
                        initial={{ opacity: 0, scale: 0.3, y: -12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                      >
                        <span className="vlg-mappin__dot" />
                        <span className="vlg-mappin__label">
                          {String(pin.name || '').slice(0, 22)}
                          {pin.rating ? ` ★${Number(pin.rating).toFixed(1)}` : ''}
                        </span>
                      </motion.div>
                    </div>
                  ))}
                </div>
                <p className="vlg-stage__caption">
                  {pins.length > 0 && visiblePins > 0
                    ? `Found ${visiblePins} competitor${visiblePins === 1 ? '' : 's'} near ${city || 'your area'}`
                    : `Locating competitors near ${city || 'your area'}…`}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default ScanningPhase;
